import { FormattedMessage, useIntl } from 'react-intl';
import { useWatch } from 'react-hook-form';

import InfoIcon from '@/assets/icons/information.svg';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { getChannelName } from '@/utils/channel-names';

import { ZIPRECRUITER_BUDGET_PRESETS } from '../validation/constants';
import { useChannelFormContext } from './channel-form-context';

interface ZipRecruiterBudgetProps {
  channelKey: string;
}

export function ZipRecruiterBudget({ channelKey }: ZipRecruiterBudgetProps) {
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const { setValue } = hookForm;
  const { handleChannelBudgetChange } = useChannelFormContext();

  const {
    openingBudgets,
    budgetAllocation,
    channelBudget,
    slug: channelSlug,
  } = useWatch({ control: hookForm.control, name: `channels.${channelKey}` });

  const zipRecruiterBudget =
    Object.values(openingBudgets).at(-1) ??
    ZIPRECRUITER_BUDGET_PRESETS[0].value;

  const handleZipRecruiterApply = (selected: number[]) => {
    const value = selected[0];
    const openingCount = Object.keys(openingBudgets).length;
    setValue(`channels.${channelKey}.channelBudget`, value * openingCount);
    handleChannelBudgetChange();
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Typography variant="subtitle2">
            {budgetAllocation === 'evenly' ? (
              <FormattedMessage
                defaultMessage="Spend per opening"
                description="Label for budget input"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Total spend"
                description="Label for budget input"
              />
            )}{' '}
            <span className="text-fx-rose">*</span>
          </Typography>
          <Tooltip
            content={intl.formatMessage(
              {
                defaultMessage:
                  '{channelName} offers a fixed budget of {singleBoost} & {doubleBoost} per opening and cannot be changed.',
                description:
                  'Explains fixed ZipRecruiter boost tiers next to the budget field',
              },
              {
                channelName: getChannelName(channelSlug),
                singleBoost: ZIPRECRUITER_BUDGET_PRESETS[0].label,
                doubleBoost: ZIPRECRUITER_BUDGET_PRESETS[1].label,
              },
            )}
          >
            <img src={InfoIcon} alt="Info" />
          </Tooltip>
        </div>
        {budgetAllocation === 'evenly' ? (
          <Select<number>
            id="channel-budget-preset"
            options={ZIPRECRUITER_BUDGET_PRESETS}
            selectedValues={[zipRecruiterBudget]}
            onApply={handleZipRecruiterApply}
            isMultiSelect={false}
            placeholder="Select"
            className="rounded-sm"
          />
        ) : (
          <Input value={channelBudget ?? ''} readOnly placeholderLabel="$" />
        )}
      </div>
    </>
  );
}
